import { Box, IconButton, LinearProgress, Stack, Tooltip } from "@mui/material";
import { DataGrid } from '@mui/x-data-grid'
import { useDispatch, useSelector } from "react-redux";
import { searchSelectedStaff, searchSelf } from "store/leave/punch-records";
import { useEffect } from "react";
import { capitalize } from "lodash";
import { useTranslation } from "react-i18next";
import {
    AccessTime,
    BeachAccessOutlined,
    MoreTimeOutlined,
    EventBusyOutlined,
    HourglassEmptyOutlined,
    InfoOutlined,
    LockOpenOutlined,
    LockOutlined,
    MailOutline,
} from "@mui/icons-material";
import { useBasePath, useSession } from "components/Providers";
import { useNavigate } from "react-router-dom";
import NoRecords from "components/NoRecords";
import dayjs from "dayjs";


export default function PunchRecordsList({ selectedTeacher, days, mode }) {
    const punchRecords = useSelector(state => state.leave.punchRecords.list)
    const calendarEvents = useSelector(state => state.leave.punchRecords.calendarEvents) ?? []
    const leaveRecords = useSelector(state => state.leave.punchRecords.leaveRecords) ?? []
    const punchRequests = useSelector(state => state.leave.punchRecords.punchRequests) ?? []
    const recordsLoading = useSelector(state => state.leave.punchRecords.loading)
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const basePath = useBasePath()
    const navigate = useNavigate()
    const personId = selectedTeacher?.personId
    const { campus } = useSession()
    const themeMode = useSelector(state => state.session.appearance)
    const dark = themeMode === 'dark'

    useEffect(() => {
        const startDate = days[0].PunchDate
        const endDate = days[days.length - 1].PunchDate
        if (mode === 'self') {
            dispatch(searchSelf({ startDate, endDate }))
        } else {
            dispatch(searchSelectedStaff({ personId, startDate, endDate }))
        }
    }, [personId, days, mode])

    const mergedRecords = days.map(day => {
        const punch = punchRecords?.find(r => r.PunchDate === day.PunchDate) || {}

        const holiday = calendarEvents.find(ev =>
            day.PunchDate >= ev.startDate && day.PunchDate <= ev.endDate &&
            (!ev.locationIds?.length || ev.locationIds.includes(campus?.id))
        )

        const leave = leaveRecords.find(r => dayjs(r.leaveDate).isSame(dayjs(day.PunchDate), 'day'))

        const permRequest = punchRequests.find(r => dayjs(r.date).isSame(dayjs(day.PunchDate), 'day') && r.type === 'permission')
        const manualRequest = punchRequests.find(r => dayjs(r.date).isSame(dayjs(day.PunchDate), 'day') && (r.type === 'in' || r.type === 'out'))

        return {
            ...day,
            ...punch,
            isHoliday: !!holiday,
            holidayName: holiday?.name,
            leaveRequest: leave ? { status: leave.approvalStatusId, typeName: leave.leaveRequestType?.name, data: leave } : undefined,
            punchPermission: permRequest ? { status: permRequest.status, data: permRequest } : undefined,
            manualPunch: manualRequest ? { data: manualRequest } : undefined,
        }
    })

    const tableColumns = [
        {
            field: 'DayOfWeek',
            headerName: t('Day'),
            flex: 1,
            editable: false,
            sortable: false,
            renderCell: params => params.row.DayOfWeek || ' '
        },
        {
            field: 'PunchDate',
            headerName: t('Punch Date'),
            flex: 1,
            editable: false,
            sortable: false
        },
        {
            field: 'PunchIn',
            headerName: t('Punch-In'),
            flex: 1,
            editable: false,
            sortable: false
        },
        {
            field: 'PunchOut',
            headerName: t('Punch-Out',),
            flex: 1,
            editable: false,
            sortable: false
        },
        {
            field: 'Duration',
            headerName: t('Total Time'),
            flex: 1,
            editable: false,
            sortable: false,
            renderCell: ({ row }) => {
                if (!row.PunchIn || !row.PunchOut || !row.PunchDate) return '—'
                const parseTime = (date, timeStr) => {
                    const [time, period] = timeStr.trim().split(' ')
                    const [hr, min, sec] = time.split(':').map(Number)
                    let hour = hr % 12
                    if (period?.toUpperCase() === 'PM') hour += 12
                    return dayjs(date).hour(hour).minute(min).second(sec)
                }
                const inTime = parseTime(row.PunchDate, row.PunchIn)
                const outTime = parseTime(row.PunchDate, row.PunchOut)
                if (!inTime.isValid() || !outTime.isValid()) return '—'
                const diffMins = outTime.diff(inTime, 'minute')
                if (diffMins <= 0) return '—'
                const h = Math.floor(diffMins / 60)
                const m = diffMins % 60
                return h > 0 ? `${h}h ${m}m` : `${m}m`
            },
        },
        {
            field: 'notes',
            headerName: t('Notes'),
            flex: 0.5,
            editable: false,
            sortable: false,
            renderCell: ({ row }) => {
                const badge = (key, Icon, title, status = null) => {
                    const pending = status === 'pending'
                    const denied = status === 'denied'
                    const iconColor = pending ? 'warning.main'
                        : denied ? 'error.main'
                        : dark ? 'grey.300' : 'text.secondary'
                    const bgcolor = pending
                        ? (dark ? 'rgba(237,108,2,0.12)' : 'rgba(237,108,2,0.08)')
                        : denied
                        ? (dark ? 'rgba(211,47,47,0.12)' : 'rgba(211,47,47,0.08)')
                        : (dark ? 'rgba(255,255,255,0.07)' : 'grey.100')
                    const border = pending ? '1.5px dashed'
                        : denied ? '1.5px solid'
                        : '1px solid transparent'
                    const borderColor = pending ? (dark ? 'warning.dark' : 'warning.main')
                        : denied ? (dark ? 'error.dark' : 'error.main')
                        : 'transparent'
                    return (
                        <Tooltip key={key} arrow title={title} placement="top">
                            <Box sx={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                width: 26, height: 26, borderRadius: '50%', cursor: 'default',
                                bgcolor, border, borderColor,
                                transition: 'background-color 0.15s, border-color 0.15s, transform 0.15s',
                                '&:hover': { bgcolor: dark ? 'rgba(255,255,255,0.14)' : 'primary.main', transform: 'scale(1.2)' },
                            }}>
                                <Icon sx={{ fontSize: 22, color: iconColor }} />
                            </Box>
                        </Tooltip>
                    )
                }
                const icons = []
                if (row.leaveRequest) {
                    const s = row.leaveRequest.status
                    const LeaveIcon = s === 'pending' ? HourglassEmptyOutlined : EventBusyOutlined
                    const label = `${row.leaveRequest.typeName || t('Leave')}${s ? ` · ${capitalize(s)}` : ''}`
                    icons.push(badge('leave', LeaveIcon, label, s))
                }
                if (row.isHoliday)
                    icons.push(badge('holiday', BeachAccessOutlined, row.holidayName || t('Holiday')))
                if (row.punchPermission) {
                    const s = row.punchPermission.status
                    const PermIcon = s === 'approved' ? LockOpenOutlined : LockOutlined
                    icons.push(badge('perm', PermIcon, `${t('Punch Permission')} · ${capitalize(s || '')}`, s))
                }
                if (row.manualPunch) {
                    const s = row.manualPunch.status
                    icons.push(badge('manual', MoreTimeOutlined, `${t('Manual Punch')} · ${capitalize(s || '')}`, s))
                }
                
                if (!icons.length) return null
                return <Stack direction="row" gap={0.5} alignItems="center" sx={{ height: '100%' }}>{icons}</Stack>
            },
        },
        {
            field: 'action',
            headerName: t('Action'),
            flex: 1,
            editable: false,
            sortable: false,
            renderCell: params => (
                <>
                    <IconButton
                        onClick={() => navigate(`${basePath}/${params.row.customId}`, { state: { personId, date: params.row.PunchDate, data: params.row, selectedTeacher } })}
                        size="small"
                    >
                        <AccessTime />
                    </IconButton>
                    <IconButton
                        onClick={() => navigate(`${basePath}/${params.row.customId}`, { state: { personId, date: params.row.PunchDate, isCorrection: true, data: params.row, selectedTeacher } })}
                        size="small"
                    >
                        <MailOutline />
                    </IconButton>
                </>
            )
        }
    ]

    return (
        <Box ml={1} mt={2}>
            {recordsLoading ? (
                <LinearProgress />
            ) : (
                <DataGrid
                    rows={mergedRecords?.map((record, index) => ({ ...record, customId: `record-${index + 1}` }))}
                    columns={tableColumns}
                    getRowId={(row) => row.customId}
                    autoHeight
                    disableSelectionOnClick
                    disableColumnMenu
                    hideFooter
                    getRowClassName={({ row }) => ['Saturday', 'Sunday'].includes(row.DayOfWeek) ? 'weekend-row' : ''}
                    sx={{ '& .weekend-row': { bgcolor: dark ? 'rgba(255,255,255,0.04)' : '#f5f5f5' }, '& .weekend-row:hover': { bgcolor: dark ? 'rgba(255,255,255,0.08) !important' : '#eeeeee !important' } }}
                    slots={{
                        noRowsOverlay: () => (
                            <NoRecords
                                message={selectedTeacher ? t('No punch records found') : t('Please select a staff member to view punch records')}
                                icon={<InfoOutlined sx={{ fontSize: 40, mb: 1, color: "grey" }} />}
                            />

                        ),
                        toolbar: null,
                    }}
                />
            )}

        </Box>
    )
}
